<template>
  <v-container fluid>
    <v-card class="pa-4">
      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-icon color="#ce501b" size="large" class="mr-2">mdi-file-lock</v-icon>
        <h2 class="text-h5">Permiso de Plantillas</h2>
      </div>

      <!-- Asignar Plantilla -->
      <v-card elevation="2" class="pa-4 mb-4">
        <h3 class="text-subtitle-1 mb-4">Asignar Plantilla</h3>

        <v-row>
          <!-- Selector de Usuario -->
          <v-col cols="12" md="5">
            <v-select
              v-model="usuarioSeleccionado"
              :items="usuarios"
              item-title="usuario"
              item-value="idUsuario"
              label="Seleccione Grupo o Usuario:"
              variant="outlined"
              density="comfortable"
              @update:model-value="cargarPlantillasAsignadas"
              clearable
            ></v-select>
          </v-col>

          <!-- Selector de Plantilla -->
          <v-col cols="12" md="5">
            <v-select
              v-model="plantillaSeleccionada"
              :items="plantillasDisponibles"
              item-title="plantilla"
              item-value="idPlantilla"
              label="Seleccione Plantilla:"
              variant="outlined"
              density="comfortable"
              :disabled="!usuarioSeleccionado"
              clearable
            ></v-select>
          </v-col>

          <!-- Botón Asignar -->
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              color="#ce501b"
              block
              @click="asignarPlantilla"
              :loading="asignando"
              :disabled="!usuarioSeleccionado || !plantillaSeleccionada"
              elevation="2"
            >
              Asignar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Tabla de Plantillas Asignadas -->
      <v-card elevation="2">
        <v-table class="plantillas-table">
          <thead>
            <tr>
              <th class="text-center">Código</th>
              <th>Plantilla</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(plantilla, index) in plantillasAsignadas"
              :key="index"
              class="plantilla-row"
            >
              <!-- Código -->
              <td class="text-center">
                <v-chip color="primary" size="small" label>
                  {{ plantilla.codigo }}
                </v-chip>
              </td>

              <!-- Plantilla -->
              <td>
                <div class="d-flex align-center">
                  <v-icon class="mr-2" size="small" color="blue-grey">mdi-file-document</v-icon>
                  <strong>{{ plantilla.plantilla }}</strong>
                </div>
              </td>

              <!-- Acciones -->
              <td class="text-center">
                <v-btn
                  icon
                  size="small"
                  color="red"
                  variant="text"
                  @click="confirmarEliminar(plantilla)"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                  <v-tooltip activator="parent" location="top">Eliminar asignación</v-tooltip>
                </v-btn>
              </td>
            </tr>

            <!-- Mensaje cuando no hay datos -->
            <tr v-if="plantillasAsignadas.length === 0">
              <td colspan="3" class="text-center py-8 text-grey">
                <v-icon size="48" color="grey">mdi-file-document-outline</v-icon>
                <p class="mt-2">Ningún dato disponible en esta tabla</p>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

// Variables
const usuarioSeleccionado = ref(null)
const plantillaSeleccionada = ref(null)
const usuarios = ref([])
const plantillasDisponibles = ref([])
const plantillasAsignadas = ref([])
const asignando = ref(false)

onMounted(async () => {
  await cargarUsuarios()
  await cargarPlantillas()
})

// Cargar lista de usuarios
const cargarUsuarios = async () => {
  try {
    const response = await axios.get('/api/Users/list-usuarios-grupos')
    
    if (response.data.success) {
      usuarios.value = response.data.data.filter(u => u.idUsuario !== '00001') // No mostrar el usuario admin
      //console.log('Usuarios cargados:', usuarios.value.length)     //Usarlo solo para ver los errores en el testeo de Software
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudieron cargar los usuarios',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al cargar usuarios:', error)     //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar los usuarios',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
      }
    })
  }
}

// Cargar lista de plantillas
const cargarPlantillas = async () => {
  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      return
    }

    const response = await axios.get(
      `/api/Plantillas/list-plantillas-selector?idUsuarioSesion=${idUsuarioSesion}`
    )
    
    if (response.data.success) {
      plantillasDisponibles.value = response.data.data
      //console.log('Plantillas cargadas:', plantillasDisponibles.value.length)       //Usarlo solo para ver los errores en el testeo de Software
    }
  } catch (error) {
    //console.error('Error al cargar plantillas:', error)      //Usarlo solo para ver los errores en el testeo de Software
  }
}

// Cargar plantillas asignadas al usuario seleccionado
const cargarPlantillasAsignadas = async () => {
  if (!usuarioSeleccionado.value) {
    plantillasAsignadas.value = []
    return
  }

  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      return
    }

    //console.log('Cargando plantillas para usuario:', usuarioSeleccionado.value)       //Usarlo solo para ver los errores en el testeo de Software

    // ✅ Usar list-plantillas-selector con el usuario seleccionado
    const response = await axios.get(
      `/api/Plantillas/list-plantillas-selector?idUsuarioSesion=${usuarioSeleccionado.value}`
    )

    if (response.data.success) {
      // Mapear los datos para la tabla
      plantillasAsignadas.value = response.data.data.map(p => ({
        codigo: p.idPlantilla,
        plantilla: p.plantilla
      }))
      
    //console.log('Plantillas asignadas:', plantillasAsignadas.value.length)     //Usarlo solo para ver los errores en el testeo de Software
    }
  } catch (error) {
    //console.error('Error al cargar plantillas asignadas:', error)         //Usarlo solo para ver los errores en el testeo de Software
    plantillasAsignadas.value = []
  }
}

// Asignar plantilla al usuario
const asignarPlantilla = async () => {
  if (!usuarioSeleccionado.value || !plantillaSeleccionada.value) {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ce501b',
      title: 'Advertencia',
      text: 'Debe seleccionar un usuario y una plantilla',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
    return
  }

  asignando.value = true

  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      asignando.value = false
      return
    }

    const payload = {
      idUsuario: usuarioSeleccionado.value,
      idPlantilla: plantillaSeleccionada.value
    }

    //console.log('Asignando plantilla:', payload)    //Usarlo solo para ver los errores en el testeo de Software

    const response = await axios.post(
      `/api/Plantillas/asignar-plantilla?idUsuarioSesion=${idUsuarioSesion}`,
      payload
    )

    if (response.data.success) {
      await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        iconColor: '#ce501b',
        text: 'Plantilla asignada correctamente',
        timer: 2500,
        confirmButtonColor: '#ce501b',
        customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar'
        },
        showConfirmButton: true
      })

      // Limpiar selector de plantilla
      plantillaSeleccionada.value = null

      // Recargar plantillas asignadas
      await cargarPlantillasAsignadas()
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudo asignar la plantilla',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        },
      })
    }
  } catch (error) {
    //console.error('Error al asignar plantilla:', error)  //Usarlo solo para ver los errores en el testeo de Software
    
    // Manejo específico de error cuando ya existe
    if (error.response?.status === 400) {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ce501b',
        title: 'Advertencia',
        confirmButtonColor: '#ce501b',
        customClass: {
          title: 'swal-title-custom',
          confirmButton: 'swal-btn-confirmar'
        },
        text: error.response?.data?.mensaje || 'Esta plantilla ya está asignada',
      })
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'Hubo un problema al asignar la plantilla',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } finally {
    asignando.value = false
  }
}


// Confirmar eliminación de asignación
const confirmarEliminar = async (plantilla) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas quitar la plantilla "${plantilla.plantilla}" de este usuario?`,
    icon: 'warning',
    iconColor: '#ce501b',
    showCancelButton: true,
    confirmButtonColor: '#ce501b',
    cancelButtonColor: '#9B9B9B',
    confirmButtonText: 'Sí, quitar',
    cancelButtonText: 'Cancelar',
    customClass: {
        title: 'swal-title-custom',
          container: 'swal-sobre-modal',
          confirmButton: 'swal-btn-confirmar',
          cancelButton: 'swal-btn-cancelar'
        }
  })

  if (result.isConfirmed) {
    eliminarAsignacion(plantilla)
  }
}

// Eliminar asignación de plantilla
const eliminarAsignacion = async (plantilla) => {
  //console.log('Eliminar asignación:', plantilla)        //Usarlo solo para ver los errores en el testeo de Software
  
  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        iconColor: '#ce501b',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      return
    }

    // ✅ Usar el endpoint correcto
    const response = await axios.delete(
      `/api/Plantillas/eliminar-asignacion/${usuarioSeleccionado.value}/${plantilla.codigo}?idUsuarioSesion=${idUsuarioSesion}`
    )

    //console.log('Respuesta eliminación:', response.data)       //Usarlo solo para ver los errores en el testeo de Software

    if (response.data.success) {
      await Swal.fire({
        icon: 'success',
        iconColor: '#ce501b',
        title: '¡Eliminado!',
        text: 'Asignación eliminada exitosamente.',
        timer: 2500,
        showConfirmButton: true,
        confirmButtonColor: '#ce501b',
          customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal',
          confirmButton: 'swal-btn-confirmar'
        }
      })

      // Recargar plantillas asignadas
      await cargarPlantillasAsignadas()
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudo eliminar la asignación',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al eliminar asignación:', error)    //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: error.response?.data?.mensaje || 'Hubo un problema al eliminar la asignación',
      customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
    })
  }
}




</script>

<style scoped>
.plantillas-table {
  background-color: transparent;
}

.plantillas-table th {
  background-color: #def2ff !important;
  font-weight: bold !important;
  color: #37495f !important;
  white-space: nowrap;
}

.plantilla-row {
  transition: background-color 0.2s ease;
}

.plantilla-row:hover {
  background-color: rgba(59, 130, 200, 0.05);
}

.plantillas-table td {
  padding: 12px 16px !important;
}
</style>

<style>

.swal-title-custom {
  color: #004d82 !important;
  font-size: 1.3rem !important;
}

.swal-text-custom {
  color: #555555 !important;
  font-size: 0.95rem !important;
}

.swal-btn-custom {
  background-color: #ce501b !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  padding: 8px 24px !important;
}

.swal-btn-custom:hover {
  background-color: #a83d14 !important;
}

.swal-btn-confirmar {
  color: white !important;
}

</style>

<style>
.swal-sobre-modal {
  z-index: 10000 !important;
}
</style>